import {useState} from 'react';

import {TZDate} from '@date-fns/tz';
import {
  addHours,
  getHours,
  getMinutes,
  isPast,
  //@ts-expect-error types missing for unknown reasons
  roundToNearestHours,
  set,
} from 'date-fns';

type Time = {
  hours: number;
  minutes: number;
};

export const TIME_RANGE = 'time-range';
export const SINGLE_TIME = 'single-time';
export const NOW = 'now';
export const ONE_OFF = 'one-off';

type PendingScheduleState =
  | {type: typeof ONE_OFF; day: Date; time: Time} // send-later
  | {type: typeof NOW} // send-now
  | {type: typeof TIME_RANGE; from: Time; to: Time} // automated-time-range
  | {type: typeof SINGLE_TIME; time: Time}; // automated-specific-time

const isValidTimeRange = (from: Time, to: Time) => {
  if (from.hours > to.hours) {
    return false;
  } else if (from.hours === to.hours) {
    return from.minutes < to.minutes;
  } else {
    return true;
  }
};

const usePendingSchedule = (
  timezone: string,
  isAutomatedDeliveryDisabled: boolean
) => {
  const [pendingSchedule, setPendingSchedule] =
    useState<PendingScheduleState | null>(() =>
      isAutomatedDeliveryDisabled ? {type: NOW} : null
    );

  const selectAutomated = (type: typeof TIME_RANGE | typeof SINGLE_TIME) => {
    const fromDate: Date = roundToNearestHours(TZDate.tz(timezone), {
      roundingMethod: 'ceil',
    });

    if (type === SINGLE_TIME) {
      setPendingSchedule({
        type: SINGLE_TIME,
        time: {hours: getHours(fromDate), minutes: getMinutes(fromDate)},
      });
    } else {
      const toDate = addHours(fromDate, 1);
      setPendingSchedule({
        type: TIME_RANGE,
        from: {hours: getHours(fromDate), minutes: getMinutes(fromDate)},
        to: {hours: getHours(toDate), minutes: getMinutes(toDate)},
      });
    }
  };

  const selectOneOff = (type: typeof NOW | typeof ONE_OFF) => {
    if (type === NOW) {
      setPendingSchedule({type: NOW});
    } else {
      const nextHour: Date = roundToNearestHours(TZDate.tz(timezone), {
        roundingMethod: 'ceil',
      });
      setPendingSchedule({
        type: ONE_OFF,
        day: nextHour,
        time: {hours: getHours(nextHour), minutes: getMinutes(nextHour)},
      });
    }
  };

  const selectFromTime = (from: Partial<Time>) => {
    setPendingSchedule((prev) =>
      prev?.type === TIME_RANGE
        ? {...prev, from: {...prev.from, ...from}}
        : prev
    );
  };

  const selectToTime = (to: Partial<Time>) => {
    setPendingSchedule((prev) =>
      prev?.type === TIME_RANGE ? {...prev, to: {...prev.to, ...to}} : prev
    );
  };

  const selectTime = (time: Partial<Time>) => {
    setPendingSchedule((prev) =>
      prev?.type === ONE_OFF || prev?.type === SINGLE_TIME
        ? {...prev, time: {...prev.time, ...time}}
        : prev
    );
  };

  const selectDay = (day: Date) => {
    setPendingSchedule((prev) =>
      prev?.type === ONE_OFF ? {...prev, day: new TZDate(day, timezone)} : prev
    );
  };

  let isInvalidSchedule = false;
  if (pendingSchedule?.type === TIME_RANGE) {
    isInvalidSchedule = !isValidTimeRange(
      pendingSchedule.from,
      pendingSchedule.to
    );
  }
  if (pendingSchedule?.type === ONE_OFF) {
    isInvalidSchedule = isPast(
      set(pendingSchedule.day, {
        hours: pendingSchedule.time.hours,
        minutes: pendingSchedule.time.minutes,
      })
    );
  }

  return {
    pendingSchedule,
    isInvalidSchedule,
    selectAutomated,
    selectOneOff,
    selectDay,
    selectFromTime,
    selectToTime,
    selectTime,
  };
};

export default usePendingSchedule;
